import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import { Button, TextField, Tooltip } from '@mui/material';
import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import { format, getTime } from 'date-fns';
import React, { useCallback, useContext, useState } from 'react';
import { updateUserItem } from '../api/auth';
import { revokeRequestItem } from '../api/orders';
import { AccountContext } from '../context/account';
import { ExploreContext } from '../context/explore';
import { MapDataContext } from '../context/map-data';
import { OrderRequestItem, ProjectItem as ProjectItemInterface } from '../core/global';
import styles from "../styles/components/project-item.module.scss";
import { LatLonBanner } from './mouse-coordinates';
import { PanToLocationButton } from './pan-to-location-button';
import { ProjectItemDownloadButtons } from './project-item-download';
import { ProjectItemOrderInfo } from './project-item-order-info';
import { ItemShareButton } from './project-item-share';
import { ProjectSelector } from './project-selector';

interface EditItemProps {
  order_item_id: any,
  fieldTitle: any,
  project_id: any,
  setItemProjectId: any, 
  setLoading: any,
  setEditMode: any,
  setFieldTitle: any,
  itemProjectId: any,
}

const EditItem = ({
  order_item_id, 
  fieldTitle, 
  project_id, 
  setItemProjectId, 
  setLoading, 
  setEditMode, 
  setFieldTitle, 
  itemProjectId,
}: EditItemProps) => {
  const {
    refetchProjects,
    setSelectedPoint,
  } = useContext(AccountContext);

  const saveUpdates = useCallback(async (new_project_id: null | string | number, name: string | null) => { 
    setLoading(true)
    await updateUserItem({
      order_item_id,
      existing_project_id: project_id ? project_id : null,
      new_project_id: new_project_id ? new_project_id : null,
      name
    })
    refetchProjects(() => {
      setEditMode(null)
      setFieldTitle(null)
      setItemProjectId(null)
      setLoading(false)
      setSelectedPoint(null)
    })
  }, [])

  return <>
    <div className={styles.row} key={order_item_id}>
      <TextField
        className={styles.input}
        id="title"
        name="title"
        label="Location title"
        value={fieldTitle}
        autoFocus
        size="small"
        fullWidth
        variant="outlined"
        onChange={(event: React.SyntheticEvent) => {
          // @ts-ignore
          setFieldTitle(event.currentTarget.value)
        }}
        onKeyUp={(event: React.KeyboardEvent) => {
          // check for enter
          if (event.key === 'Enter') {
            saveUpdates(itemProjectId, fieldTitle)
          }
        }}
        onClick={() => {
          // setDatePickerOpen(true)
        }}
        // color={titleError ? 'error' : 'primary'}
      />
      <ProjectSelector
        className={styles.input}
        showCreateNew
        compact
        projectId={project_id}
        onChange={(id: number | null) => {
          setItemProjectId(id)
        }}
      />
      <IconButton
        size="small"
        color="success"
        onClick={(e: React.SyntheticEvent) => {
          e.preventDefault()
          saveUpdates(itemProjectId, fieldTitle)
          
        }}
      >
        <CheckIcon />
      </IconButton>
      <IconButton
        size="small"
        color="warning"
        onClick={(e: React.SyntheticEvent) => {
          e.preventDefault()
          setEditMode(null)
          setFieldTitle(null)
          setItemProjectId(null)
          setSelectedPoint(null)
        }}
      >
        <CloseIcon />
      </IconButton>
    </div>
  </>
}

interface ProjectItemProps {
  data: ProjectItemInterface[]
  isPurchaseRequest?: boolean
  viewOnly?: boolean
}

export const ProjectItem = ({
  data,
  isPurchaseRequest = false,
  viewOnly,
}: ProjectItemProps) => {
  const [loading, setLoading] = useState(false)
  const [itemProjectId, setItemProjectId] = useState<number | null>(null)
  const [editMode, setEditMode] = useState<number |null>(null)
  const [fieldTitle, setFieldTitle] = useState<string | null>(null)
  const [deleteItem, setDeleteItem] = useState<number | null>(null)

  const { 
    setExploreLocationParams,
  } = useContext(ExploreContext);

  const {
    setMapCenter,
    updateMeshData,
    setDataSourceModel,
    dataSourceModel,
    dataSources,
  } = useContext(MapDataContext);

  const {
    setSelectedPoint,
    setActiveOrderId,
    refetchProjects,
    refetchUserProjects,
    account,
  } = useContext(AccountContext);


  return (
    <div className={styles.wrapper}>
      {data.map( (item: ProjectItemInterface) => {
        const {
          lat,
          lon,
          name,
          id,
          project_id,
          order_item_id,
          order_id,
          model,
          provider,
          geography,
          version,
          download,
          path,
          uid,
          is_api_acquisition,
        } = item
          const useLabel = name || `${lat} ${lon}`
          const projectModel = {
            key: `${provider}-${model}-${geography}-${version}`,
            provider,
            model,
            geography,
            version,
          }

          const status = item.status || 'pending'
          // User can add and move their own item even inside shared folders
          const canEditItem = !viewOnly || account.uid === uid
          return (
            <div key={id} className={styles.column}>

              {id === editMode ?
                <EditItem
                  order_item_id={order_item_id}
                  fieldTitle={fieldTitle}
                  project_id={project_id}
                  setItemProjectId={setItemProjectId}
                  setLoading={setLoading}
                  setEditMode={setEditMode}
                  setFieldTitle={setFieldTitle}
                  itemProjectId={itemProjectId}
                />
              :
                <div className={styles.row}>
                  {!name ?    
                    <p className={styles.modelName} title={useLabel}>{useLabel}</p>
                  :
                  <Badge
                    className={styles.badge}
                    badgeContent={`${lat} ${lon}`}
                    color="info"
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                  >
                    <p className={styles.modelName} title={useLabel}>{useLabel}</p>
                  </Badge>
                  }
                </div>
              }
              <p>Model: 
                <span className={styles.modelLabel}>
                  {(dataSources && Object.prototype.hasOwnProperty.call(dataSources, `${provider}-${model}-${geography}-${version}`)) ? dataSources[`${provider}-${model}-${geography}-${version}`].name : `${provider}-${model}-${geography}-${version}`}
                </span>
              </p>
              <LatLonBanner lat={lat} lon={lon} title="Loc: " />
              {
                item.created_at && <p><strong>Requested on:</strong> {format(new Date(item.created_at), 'dd MMMM yyyy')}</p>
              }
              {
                isPurchaseRequest && <p><strong>Requested status:</strong> {status}</p>
              }
              <div className={styles.buttons}>
                {!isPurchaseRequest ?
                  <div>
                    <ProjectItemDownloadButtons item={item} />
                    {!is_api_acquisition && <Tooltip
                      title={'Edit'} 
                      placement="bottom" 
                      arrow
                    >
                      <span>
                        <IconButton
                          disabled={!canEditItem}
                          size="small"
                          onClick={(e: React.SyntheticEvent) => {
                            e.preventDefault()
                            setEditMode(id)
                            setFieldTitle(useLabel)
                            setItemProjectId(project_id || 0)
                            setMapCenter({location: {lat, lng: lon}})
                            setSelectedPoint([lat, lon])
                          }}
                        >
                          <EditIcon style={{color: !canEditItem ? 'grey' : 'black'}} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    }
                    <Tooltip
                      title={'Visualise'} 
                      placement="bottom" 
                      arrow
                    >
                      <IconButton
                        size="small"
                        onClick={(e: React.SyntheticEvent) => {
                          e.preventDefault()
                          const startDate = getTime(new Date(item.start_date))
                          const endDate = getTime(new Date(item.end_date))
                          const [,orderId,filename] = path?.split('/') || []
                          setExploreLocationParams({
                            model,
                            provider,
                            geography,
                            lat,
                            lon,
                            startDate,
                            endDate,
                            orderId,
                            version,
                            filename,
                          })

                          // update current user model
                          if (dataSourceModel 
                            && dataSourceModel.key !== projectModel.key
                          ) {
                            setDataSourceModel(projectModel)
                          }
                          updateMeshData({coords:`${lat}, ${lon}`, useModel: projectModel})
                          setMapCenter({location: {lat, lng: lon}})
                          setSelectedPoint([lat, lon])
                          
                        }}
                      >
                        <QueryStatsIcon sx={{color: '#000'}} />
                      </IconButton>
                    </Tooltip>
                    <ProjectItemOrderInfo orderId={order_id} />
                    {!is_api_acquisition && <ItemShareButton itemId={id} type="item" />}
                  </div>
                  :
                  <div style={{marginLeft: "-8px"}}>
                    <Tooltip
                      title={'Visualise'} 
                      placement="bottom" 
                      arrow
                    >
                      <IconButton
                        size="small"
                        onClick={(e: React.SyntheticEvent) => {
                          e.preventDefault()
                          
                          const [,orderId,filename] = path?.split('/') || []
                          setExploreLocationParams({
                            model,
                            provider,
                            geography,
                            lat,
                            lon,
                            orderId,
                            version,
                            filename,
                          })

                          updateMeshData({coords: `${lat}, ${lon}`, useModel: projectModel})
                          setMapCenter({location: {lat, lng: lon}})
                          setSelectedPoint([lat, lon])
                        }}
                      >
                        <TravelExploreIcon sx={{color: '#000'}} />
                      </IconButton>
                    </Tooltip>
                  </div>
                }
                <>
                  {isPurchaseRequest && 
                    <div style={{width: "100%", textAlign: "left"}}>
                    {deleteItem === null ?
                        <IconButton
                          size='small'
                          onClick={() => {
                            setDeleteItem(id)
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      : 
                      <>
                        <Button 
                          size='small'
                          color='error'
                          startIcon={<DeleteForeverIcon />}
                          onClick={async () => {
                            // setDeletingItem(id)
                            
                            if (id) {
                              await revokeRequestItem({
                                request_item_id: id,
                              });
                              refetchUserProjects();
                              refetchProjects();
                            }
                            setDeleteItem(null)
                          }}>
                          Revoke request
                        </Button>
                        <Button 
                          size='small'
                          color="success"
                          onClick={() => {
                            setDeleteItem(null)
                          }}>
                          Keep
                        </Button>
                      </>
                    }
                    </div>
                  }
                </>
                <div>
                  <PanToLocationButton
                    size="small" 
                    lat={lat} 
                    lon={lon} 
                    onClick={() => {
                      if (dataSourceModel 
                        && dataSourceModel.key !== projectModel.key
                      ) {
                        setDataSourceModel(projectModel)
                      }
                      setSelectedPoint([lat, lon])
                      updateMeshData({coords: `${lat}, ${lon}`, useModel: projectModel})
                    }}
                  />
                </div>
              </div>
            </div>
          )
        })
      }
    </div>
  )
}


interface ProjectItemPurchaseRequestProps {
  data: OrderRequestItem[]
}

export const ProjectItemPurchaseRequest = ({
  data,
}: ProjectItemPurchaseRequestProps) => {
  const [deleteItem, setDeleteItem] = useState<number | null>(null)

  const { 
    setExploreLocationParams,
  } = useContext(ExploreContext);

  const {
    setMapCenter,
    updateMeshData,
    setDataSourceModel,
    dataSourceModel,
    dataSources,
  } = useContext(MapDataContext);

  const {
    setSelectedPoint,
    setActiveOrderId,
    refetchProjects,
    refetchUserProjects,
    account,
  } = useContext(AccountContext);


  return (
    <div className={styles.wrapper}>
      {data.map( (item: OrderRequestItem) => {
        const {
          lat,
          lon,
          id,
          model,
          provider,
          geography,
          version,
        } = item
          const useLabel = `${lat} ${lon}`
          const projectModel = {
            key: `${provider}-${model}-${geography}-${version}`,
            provider,
            model,
            geography,
            version,
          }
          const status = item.status || 'pending'
          return (
            <div key={id} className={styles.column}>
              <div className={styles.row}>
                <p className={styles.modelName} title={useLabel}>{useLabel}</p>
              </div>
              <p>Model: 
                <span className={styles.modelLabel}>
                  {(dataSources && Object.prototype.hasOwnProperty.call(dataSources, `${provider}-${model}-${geography}-${version}`)) ? dataSources[`${provider}-${model}-${geography}-${version}`].name : `${provider}-${model}-${geography}-${version}`}
                </span>
              </p>
              <LatLonBanner lat={lat} lon={lon} title="Loc: " />
              {
                item.created_at && <p><strong>Requested on:</strong> {format(new Date(item.created_at), 'dd MMMM yyyy')}</p>
              }
              <p><strong>Requested status:</strong> {status}</p>
              <div className={styles.buttons}>
              <div style={{marginLeft: "-8px"}}>
                    <Tooltip
                      title={'Visualise'} 
                      placement="bottom" 
                      arrow
                    >
                      <IconButton
                        size="small"
                        onClick={(e: React.SyntheticEvent) => {
                          e.preventDefault()
                          
                          setExploreLocationParams({
                            model,
                            provider,
                            geography,
                            lat,
                            lon,
                            version,
                          })

                          updateMeshData({coords: `${lat}, ${lon}`, useModel: projectModel})
                          setMapCenter({location: {lat, lng: lon}})
                          setSelectedPoint([lat, lon])
                        }}
                      >
                        <TravelExploreIcon sx={{color: '#000'}} />
                      </IconButton>
                    </Tooltip>
                  </div>

                <div style={{width: "100%", textAlign: "left"}}>
                {deleteItem === null ?
                    <IconButton
                      size='small'
                      onClick={() => {
                        setDeleteItem(id)
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  : 
                  <>
                    <Button 
                      size='small'
                      color='error'
                      startIcon={<DeleteForeverIcon />}
                      onClick={async () => {
                        // setDeletingItem(id)
                        
                        if (id) {
                          await revokeRequestItem({
                            request_item_id: id,
                          });
                          refetchUserProjects();
                          refetchProjects();
                        }
                        setDeleteItem(null)
                      }}>
                      Revoke request
                    </Button>
                    <Button 
                      size='small'
                      color="success"
                      onClick={() => {
                        setDeleteItem(null)
                      }}>
                      Keep
                    </Button>
                  </>
                }
                </div>
                <div>
                  <PanToLocationButton
                    size="small" 
                    lat={lat} 
                    lon={lon} 
                    onClick={() => {
                      if (dataSourceModel 
                        && dataSourceModel.key !== projectModel.key
                      ) {
                        setDataSourceModel(projectModel)
                      }
                      setSelectedPoint([lat, lon])
                      updateMeshData({coords: `${lat}, ${lon}`, useModel: projectModel})
                    }}
                  />
                </div>
              </div>
            </div>
          )
        })
      }
    </div>
  )
}
